import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ClipboardList, MapPin } from 'lucide-react';
import type { TeacherTodayResponse } from '@bbl/validation/landings';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useTeacherToday } from '../lib/landings';
import { EmptyState } from '../components/ui/EmptyState';
import {
  LandingPage,
  KPIRow,
  CardsRow,
  LandingSkeleton,
  LandingError,
  LandingGuard,
  KPICard,
  LandingCard,
  LandingCardLine,
  ModuleGatedCard,
} from '../components/portal-shell';
import {
  BandChip,
  buildBreakdownTeaser,
  performanceBandValues,
  type PerformanceBandValue,
  type PerformanceBreakdownEnvelope,
} from '../components/performance';
import { formatINR } from '../lib/format';
import { TodayReportsCard } from '../components/teacher-reports/TodayReportsCard';

export const Route = createFileRoute('/_authed/today')({
  component: TodayPage,
});

function TodayPage() {
  return (
    <LandingGuard landingPath="/today">
      <TodayContent />
    </LandingGuard>
  );
}

const dayFormatter = new Intl.DateTimeFormat('en-IN', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});

const timeFormatter = new Intl.DateTimeFormat('en-IN', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: false,
});

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return timeFormatter.format(d);
};

type SessionSummary = TeacherTodayResponse['data']['classesToday'][number];

function TodayContent() {
  const user = useAuthStore((s) => s.user);
  const query = useTeacherToday({ enabled: !!user });
  const teacherId = user?.teacherId ?? null;
  const tmaScoreFromLanding = query.data?.data.tmaScore ?? null;

  // When the landing payload signals the TMA module is enabled (tmaScore is
  // not null), fetch the breakdown so we can render the band chip + teaser.
  // The landing payload alone doesn't carry the per-variable breakdown.
  const breakdownQuery = useQuery({
    queryKey: ['performance', 'breakdown', teacherId] as const,
    enabled: Boolean(teacherId) && tmaScoreFromLanding !== null,
    queryFn: ({ signal }) =>
      apiRequest<PerformanceBreakdownEnvelope>(
        `/api/performance/teachers/${encodeURIComponent(teacherId!)}/breakdown`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const today = dayFormatter.format(new Date());

  if (query.isLoading || (!query.data && !query.isError)) {
    return (
      <LandingPage
        title="Today"
        meta={user ? `${user.displayName} · ${today}` : today}
        intro="Your day at a glance, plus pending actions and your performance summary."
      >
        <LandingSkeleton />
      </LandingPage>
    );
  }
  if (query.isError || !query.data) {
    return (
      <LandingPage title="Today">
        <LandingError error={query.error} onRetry={() => query.refetch()} />
      </LandingPage>
    );
  }

  const { data } = query.data;
  const classCount = data.classesToday.length;
  const firstThreeTimes = data.classesToday
    .slice(0, 3)
    .map((c) => formatTime(c.scheduledAt))
    .join(' · ');

  return (
    <LandingPage
      title="Today"
      meta={user ? `${user.displayName} · ${today}` : today}
      intro="Your day at a glance, plus pending actions and your performance summary."
    >
      <KPIRow>
        <KPICard
          label="Classes today"
          value={classCount}
          footnote={firstThreeTimes || 'No classes scheduled'}
        />
        <KPICard
          label="Attendance pending"
          value={data.attendancePendingCount}
          footnote={data.attendancePendingCount === 0 ? 'All marked' : 'Mark from session below'}
          tone={data.attendancePendingCount > 0 ? 'warning' : 'success'}
        />
        <TmaKpiCard
          score={data.tmaScore?.score ?? null}
          bandFromLanding={data.tmaScore?.band ?? null}
          period={data.tmaScore?.period ?? null}
          breakdown={breakdownQuery.data?.breakdown ?? null}
          snapshot={breakdownQuery.data?.snapshot ?? null}
        />
        <KPICard
          label="Wallet"
          value={data.walletBalance ? formatINR(data.walletBalance.academicYearPaise) : '—'}
          footnote={
            data.walletBalance
              ? `${data.walletBalance.confirmedReferralsCount} confirmed referrals`
              : 'Wallet not enabled yet'
          }
          newFor="/me/referrals"
        />
      </KPIRow>

      <TodayReportsCard />

      <ClassTimeline sessions={data.classesToday} />

      <CardsRow>
        {data.thisWeeksQuiz ? (
          <LandingCard title="This week's quiz" newFor="/academics/assessments">
            <LandingCardLine>
              <span className="font-medium">{data.thisWeeksQuiz.title}</span>
            </LandingCardLine>
            <LandingCardLine>
              {data.thisWeeksQuiz.participantsCount} participants
              {typeof data.thisWeeksQuiz.averageScore === 'number'
                ? ` · avg ${data.thisWeeksQuiz.averageScore.toFixed(0)}`
                : ''}
            </LandingCardLine>
            <LandingCardLine last>Published this week</LandingCardLine>
          </LandingCard>
        ) : (
          <ModuleGatedCard
            title="This week's quiz"
            description="Your weekly quiz will show here once tests & quizzes are switched on."
          />
        )}

        {data.parentFeedbackAggregate ? (
          data.parentFeedbackAggregate.insufficient ? (
            <LandingCard title="Parent feedback" newFor="/me/feedback">
              <LandingCardLine last>
                Need a few more responses to show your aggregate ({' '}
                {data.parentFeedbackAggregate.responsesCount} so far).
              </LandingCardLine>
            </LandingCard>
          ) : (
            <LandingCard title="Parent feedback" newFor="/me/feedback">
              <LandingCardLine>
                <span className="font-medium tabular-nums">
                  {data.parentFeedbackAggregate.averageRating.toFixed(1)} / 5
                </span>
                <span className="text-[color:var(--color-text-muted)]">
                  {' '}
                  · {data.parentFeedbackAggregate.responsesCount} responses
                </span>
              </LandingCardLine>
              <LandingCardLine last>
                Period: {data.parentFeedbackAggregate.period}
              </LandingCardLine>
            </LandingCard>
          )
        ) : (
          <ModuleGatedCard
            title="Parent feedback"
            description="Parent feedback aggregates appear here once the Feedback module is enabled."
          />
        )}

        <LandingCard title="Weekly sheets">
          <LandingCardLine>
            <span className="tabular-nums font-medium">
              {data.weeklySheets.pendingSubmission}
            </span>{' '}
            pending submission
          </LandingCardLine>
          <LandingCardLine>
            <span className="tabular-nums font-medium">{data.weeklySheets.approved}</span>{' '}
            approved
          </LandingCardLine>
          <LandingCardLine last>
            <span className="tabular-nums font-medium">
              {data.weeklySheets.revisionRequested}
            </span>{' '}
            revision requested
          </LandingCardLine>
        </LandingCard>
      </CardsRow>
    </LandingPage>
  );
}

interface TmaKpiCardProps {
  score: number | null;
  bandFromLanding: string | null;
  period: string | null;
  breakdown: PerformanceBreakdownEnvelope['breakdown'];
  snapshot: PerformanceBreakdownEnvelope['snapshot'];
}

/**
 * TMA KPI tile on the Today landing. Combines the landing payload's score
 * (canonical & fast) with the per-variable breakdown fetched from
 * `/api/performance/teachers/:id/breakdown` so the footnote can carry the
 * band chip + a one-line strengths/weaknesses teaser.
 */
function TmaKpiCard({
  score,
  bandFromLanding,
  period,
  breakdown,
  snapshot,
}: TmaKpiCardProps) {
  // Prefer the breakdown's band when present (it's stricter typed); fall
  // back to the landing payload's band string for the initial paint. The
  // landing schema declares `band: string` so we narrow defensively to
  // avoid sending an arbitrary string into the BandChip.
  const rawBand = snapshot?.band ?? bandFromLanding;
  const band: PerformanceBandValue | null =
    rawBand && (performanceBandValues as ReadonlyArray<string>).includes(rawBand)
      ? (rawBand as PerformanceBandValue)
      : null;
  const teaser = breakdown ? buildBreakdownTeaser(breakdown) : null;

  return (
    <KPICard
      label="My TMA score"
      value={score !== null ? score.toFixed(0) : '—'}
      footnote={
        score === null
          ? 'TMA module not enabled yet'
          : (
            <span className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-2">
                {band ? <BandChip band={band} size="sm" /> : null}
                {period ? (
                  <span className="text-[color:var(--color-text-muted)]">
                    {period}
                  </span>
                ) : null}
              </span>
              {teaser ? (
                <Link
                  to="/me/performance"
                  className="text-[11px] text-gold-700 dark:text-gold-300 hover:underline"
                >
                  {teaser}
                </Link>
              ) : (
                <Link
                  to="/me/performance"
                  className="text-[11px] text-gold-700 dark:text-gold-300 hover:underline"
                >
                  View breakdown →
                </Link>
              )}
            </span>
          )
      }
      newFor="/me/performance"
    />
  );
}

function ClassTimeline({ sessions }: { sessions: ReadonlyArray<SessionSummary> }) {
  if (sessions.length === 0) {
    return (
      <LandingCard title="Class timeline">
        <EmptyState
          title="No classes today"
          description="Enjoy the breather. Your next batch will show up here on its scheduled day."
          className="py-6"
        />
      </LandingCard>
    );
  }

  return (
    <LandingCard
      title="Class timeline"
      className="md:col-span-2"
      titleAction={
        <Link
          to="/academics/attendance"
          className="text-xs font-medium text-gold-700 dark:text-gold-300 underline"
        >
          Open attendance
        </Link>
      }
    >
      <ul role="list" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {sessions.map((session) => (
          <SessionTile key={session.id} session={session} />
        ))}
      </ul>
    </LandingCard>
  );
}

function SessionTile({ session }: { session: SessionSummary }) {
  const start = formatTime(session.scheduledAt);
  const statusTone =
    session.status === 'in_progress'
      ? 'border-gold-500 ring-1 ring-gold-500/40'
      : session.status === 'completed'
        ? 'border-[color:var(--color-border)] opacity-80'
        : session.status === 'cancelled'
          ? 'border-[color:var(--color-danger)]/40'
          : 'border-[color:var(--color-border)]';
  return (
    <li
      className={
        'relative rounded-md border bg-[color:var(--color-bg-elevated)] p-3 ' + statusTone
      }
    >
      <p className="text-[11px] font-medium uppercase tracking-wider text-[color:var(--color-text-muted)]">
        {session.courseName ?? 'Class'}
      </p>
      <h4 className="mt-0.5 font-display text-base font-semibold text-[color:var(--color-text)]">
        {session.batchName}
      </h4>
      {session.lessonTitle ? (
        <p className="mt-0.5 text-[12px] text-[color:var(--color-text-muted)] truncate">
          <MapPin size={11} aria-hidden="true" className="inline -mt-0.5 mr-1" />
          {session.lessonTitle}
        </p>
      ) : null}
      <div className="mt-2 flex items-center justify-between text-[12px]">
        <span className="tabular-nums font-medium">{start}</span>
        {session.attendanceMarked ? (
          <span className="text-[color:var(--color-success)] text-[11px] font-medium">
            marked
          </span>
        ) : (
          <Link
            to="/academics/attendance"
            search={{ batchId: session.batchId, date: 'today' }}
            className="inline-flex items-center gap-1 text-gold-700 dark:text-gold-300 hover:underline"
          >
            <ClipboardList size={12} aria-hidden="true" />
            Mark
            <ChevronRight size={12} aria-hidden="true" />
          </Link>
        )}
      </div>
    </li>
  );
}

